import type { SortingState } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/core/basic/Button";
import Loading from "../../../../components/core/blocks/Loading";
import ReactTablePagination from "../../../../components/custom/growth/ProductsCVTablePagination";
import ExpanderCell from "../../../../components/custom/merchandise/cells/ExpanderCell";
import TrComponent from "../../../../components/custom/merchandise/TrComponent";
import { BulkActionButton, type BulkActionOptions } from "../../../../components/table/BulkActionButton";
import useColumnSelector from "../../../../hooks/useColumnSelector";
import useTableSettings, { type CustomFilter } from "../../../../hooks/useTableSettings";
import type React from "react";
import { useEffect, useMemo, useState, type ReactNode, type RefObject } from "react";
import { useDebounce } from "@reactuses/core";
import ReactTable, { type Column, type DerivedDataObject, type Instance, type RowInfo } from "react-table-6";
import { snakeCaseToSentenceCase } from "../../../../utils/formatText";
import { sortTableColumns } from "../../../../utils/tableUtils";
import { Checkbox } from "../../../../components/fields/base/Checkbox";
import { useAppSelector } from "../../../../redux/hooks";
import buildEditColumn, { type EditColumnOptions } from "../columns/buildEditColumn";
import buildMetricColumn, { type MetricColumn, type MetricColumnOptions } from "../columns/buildMetricColumn";
import type { MetricLabel } from "../../utils/metrics";
import { RT6FilterWidget, type ColumnOption } from "./FilterWidget";
import { extendAllEntityMetricOptions, useStandardEntityMetrics } from "./metricUtils";
import { roasToAcos } from "../../../../components/core/blocks/AdPlan/utils";
import { HStack, Wrap } from "../../../../../styled-system/jsx";
import { FaPencil } from "react-icons/fa6";
import { css } from "../../../../../styled-system/css";
import BulkEditModal from "../../../../components/core/basic/BulkEditModal";

const getExpanderColumnDefinition = (
  isExpanded: string | null,
  setIsExpanded: (value: string | null) => void,
  selectionKey: string,
): Column => ({
  id: "selector",
  Header: () => null,
  sortable: false,
  accessor: "selected",
  width: 50,
  Cell: ({ row }) => {
    return (
      <ExpanderCell row={row} isExpanded={isExpanded} setIsExpanded={setIsExpanded} valueAccessor={selectionKey} />
    );
  },

  // oxlint-disable-next-line react/jsx-no-useless-fragment
  Filter: () => <></>,
});

const getSelectorColumnDefinition = (
  selectionKey: string,
  selected: string[] | number[] = [],
  setSelected: (val: string[] | number[]) => void,
  disableSelectionList?: unknown[],
  hideSelectAll?: boolean,
): Column => ({
  id: "selector",
  Header: (cellInfo) => {
    if (hideSelectAll) {
      return null;
    }
    // For a header, `cellInfo` has `data` array, which is table's rows
    // oxlint-disable-next-line @typescript-eslint/no-explicit-any
    const data = (cellInfo as any).data as DerivedDataObject[];
    const allIds = data.map((r) => r._original?.[selectionKey]);
    const allSelected = allIds.filter((id) => !disableSelectionList?.includes(id)).every((id) => selected.includes(id));
    const someSelected = allIds.some((id) => selected.includes(id));
    return (
      <Checkbox
        size="small"
        value={
          allSelected ? true
          : someSelected ?
            "indeterminate"
          : false
        }
        onChange={(checked) => {
          if (checked) {
            if (disableSelectionList?.length) {
              const filteredIds = allIds.filter((id) => !disableSelectionList.includes(id));
              setSelected(filteredIds);
              return;
            }
            setSelected(allIds);
          } else {
            setSelected([]);
          }
        }}
      />
    );
  },
  sortable: false,
  width: 40,
  Cell: (cellInfo) => {
    const value = cellInfo.original[selectionKey];

    return (
      <Checkbox
        size="small"
        isDisabled={disableSelectionList?.includes(value)}
        value={selected.includes(value)}
        onChange={(checked) => {
          if (checked) {
            setSelected([...selected, value] as string[] | number[]);
          } else {
            setSelected(selected.filter((entry) => entry !== value) as string[] | number[]);
          }
        }}
      />
    );
  },
});

export type ColumnSpec =
  | {
      id?: string;
      key?: string;
      options?: EditColumnOptions | MetricColumnOptions;
      compareKey?: string | null;
      custom?: false;
    }
  | (Column & { custom: true; name?: MetricLabel | string });

type BulkChangeRecord = Record<number | string, Record<string, unknown>>;

interface Props<T extends object> {
  tableRef: RefObject<Instance<T> | null>;
  stats?: unknown;
  mobileStyle?: unknown;
  titleCol?: Column & { name?: MetricLabel | string; isStatic?: boolean };
  columnSpecs?: ColumnSpec[];
  data?: T[];
  getSearchCriteria: (row: unknown) => string | null;
  isLoading?: boolean;
  selected?: string[] | number[];
  setSelected?: (value: string[] | number[]) => void;
  selectionKey?: keyof T;
  handleBulkChanges?: (changes: BulkChangeRecord, clearChanges: () => void) => void;
  bulkActionOptions?: BulkActionOptions<unknown>[];
  ExpandedContent?: React.ComponentType<{ marketPlace?: unknown; channel?: unknown; row?: RowInfo; user?: unknown }>;
  isExpandable: unknown;
  isSelectable: unknown;
  hideBulkActionsButton?: boolean;
  children?: ReactNode;
  additionalFilters?: ReactNode;
  defaultSorted?: SortingState;
  isSaving?: boolean;
  tableId: string;
  defaultPageSize?: number;
  clearParentFilters?: unknown;
  hideFilters?: boolean;
  hideSearchFilter?: boolean;
  hidePagination?: boolean;
  hideBottomPagination?: boolean;
  hideFilterToggleIcon?: boolean;
  hideResetFiltersIcon?: boolean;
  isStatsLoading?: boolean;
  minRows?: number;
  tableRowDescription?: string;
  additionalFilterOptions?: ColumnOption[];
  filterControlsWidth?: string | number;
  excludedFilterColumns?: string[];
  filterWidget?: boolean;
  customFilters?: CustomFilter[];
  hideCompareValue?: boolean;
  setCustomFilters?: (updatedFilters: CustomFilter[]) => void;
  applyCustomFilters?: (data: Record<string, unknown>[], columns: unknown[]) => Record<string, unknown>[] | undefined;
  noDataText?: string;
  leftActionButton?: ReactNode;
  entityType?: string;
  groupMetrics?: boolean;
  disableSelectionList?: unknown[];
  hideSelectAll?: boolean;
  canSetPageSize?: boolean;
  extendMetrics?: boolean;
  overrideResultsCount?: number;
}

const BulkActionsTable = <T extends object>({
  tableRef,
  stats,
  titleCol,
  columnSpecs = [],
  data,
  getSearchCriteria,
  isLoading,
  selected,
  setSelected,
  selectionKey,
  handleBulkChanges = (changes, clearChanges) => {
    clearChanges();
  },
  bulkActionOptions = [],
  ExpandedContent = () => null,
  isExpandable = true,
  isSelectable = true,
  hideBulkActionsButton = false,
  children,
  additionalFilters,
  defaultSorted,
  isSaving = false,
  tableId,
  defaultPageSize = 10,
  clearParentFilters,
  hideFilters = false,
  hideSearchFilter = false,
  hidePagination = false,
  hideBottomPagination = false,
  hideFilterToggleIcon = false,
  hideResetFiltersIcon = false,
  isStatsLoading,
  minRows = 5,
  tableRowDescription,
  additionalFilterOptions,
  filterControlsWidth,
  excludedFilterColumns,
  filterWidget = false,
  customFilters = [],
  hideCompareValue = false,
  setCustomFilters = () => {},
  applyCustomFilters = (arr = []) => {
    return arr;
  },
  noDataText = "",
  leftActionButton = null,
  entityType,
  groupMetrics = true,
  disableSelectionList = [],
  hideSelectAll = false,
  canSetPageSize = true,
  extendMetrics = true,
  overrideResultsCount,
}: Props<T>) => {
  const { t } = useTranslation(["merchandising"]);
  const [searchFilter, setSearchFilter] = useState("");
  const debouncedSearch = useDebounce(searchFilter, 300);
  const [clearFilters, setClearFilters] = useState(false);
  const [isExpanded, setIsExpanded] = useState<string | null>(null);

  const [showFilters, setShowFilters] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [draftSearch, setDraftSearch] = useState(searchFilter);

  const [changes, setChanges] = useState<BulkChangeRecord>({});
  const [renderSave, setRenderSave] = useState(false);

  const marketPlace = useAppSelector((state) => state.marketPlace);
  const channel = useAppSelector((state) => state.channel);
  const user = useAppSelector((state) => state.user);

  useEffect(() => {
    setSearchFilter("");
    setShowFilters(false);
  }, [tableId]);

  const { savedTableSettings: savedTablePageSize, updateTableSettings: setSavedTablePageSize } = useTableSettings(
    tableId,
    "tablePageSize",
    defaultPageSize,
  );

  const { savedTableSettings: savedTableSortMethod, updateTableSettings: setSavedTableSortMethod } = useTableSettings(
    tableId,
    "tableSorting",
    defaultSorted ?? [],
  );

  const { savedTableSettings: savedColumnOrder, updateTableSettings: setSavedColumnOrder } = useTableSettings(
    tableId,
    "tableColOrder",
    {},
  );

  const standardMetricIds = useStandardEntityMetrics(entityType);

  const [columns, columnSelectorProps] = useColumnSelector(
    () => {
      let specifiedColumns: Column[] = columnSpecs.map((c) => {
        if (c.custom) return c;
        if (c.options?.editCell) {
          return buildEditColumn(c.options.columnTitle ?? snakeCaseToSentenceCase(c.key), marketPlace, c.key!, {
            editCell: true,
            isStatic: c.options.isStatic ?? false,
            format: c.options.format ?? "currency",
            validations: {
              minValue: c.options.validations?.minValue ?? 1,
              blank: false,
              compare: c.options.validations?.compare,
            },
            toolTip: c.options.toolTip,
            tabIndex: 2,
            overrideKey: c.options.overrideKey,
            onValue: c.options.onValue ?? "1",
            offValue: c.options.offValue ?? "0",
            // oxlint-disable-next-line @typescript-eslint/no-explicit-any
            width: c.options.format === "toggle" ? (c.options.width ?? 75) : ("inherit" as any),
            className: `${c.options.className}`,
            headerClassName: c.options.headerClassName ?? "justify-content-start text-start",
            toggleAction: c.options.toggleAction,
            strikeThroughText: c.options.strikeThroughText,
            checked: c.options.checked ?? true,
            renderPlaceholderCellContent: c.options.renderPlaceholderCellContent,
            disabled: c.options.disabled ?? false,
            getCellDisabled: c.options.getCellDisabled,
          });
        }
        return buildMetricColumn(marketPlace, c.key!, c.compareKey, c.options, hideCompareValue);
      });
      if (entityType && extendMetrics) {
        specifiedColumns = extendAllEntityMetricOptions(
          standardMetricIds,
          { total: data?.[0] },
          specifiedColumns as Array<MetricColumn<T>>,
          marketPlace,
        );
      }

      return sortTableColumns(specifiedColumns, savedColumnOrder);
    },
    [columnSpecs, stats, customFilters, standardMetricIds],
    tableId,
  );

  const filteredData = useMemo(
    () => {
      let result = data ?? [];

      if (debouncedSearch) {
        result = result.filter((row) => {
          const searchable = getSearchCriteria(row);

          if (debouncedSearch.includes(",")) {
            const matchable = debouncedSearch.split(",");
            return matchable.some((searchCriteria) => {
              return (
                searchable
                  ?.replaceAll(/\s/g, "")
                  .toLowerCase()
                  .includes(searchCriteria.replaceAll(/\s/g, "").toLowerCase()) && searchCriteria.trim().length
              );
            });
          }

          return searchable?.toLowerCase().includes(debouncedSearch.toLowerCase());
        });
      }

      result = (applyCustomFilters(result as Record<string, unknown>[], columns) ?? []) as T[];

      return result.map((row) => {
        // oxlint-disable-next-line @typescript-eslint/no-explicit-any
        const enriched: Record<string, any> = {
          ...row,
          isStatsLoading,
          ...(row[selectionKey!] === isExpanded ? { isExpanded } : undefined),
          ...changes[row[selectionKey!] as string | number],
        };
        enriched.handleUpdate = (rowInfo: DerivedDataObject, key: string, value: string) => {
          let convertedChanges = {};
          if (key === "target_roas") {
            enriched.target_roas = parseFloat(value);
            convertedChanges = { target_acos: roasToAcos(enriched.target_roas) };
          }

          if (key === "min_roas") {
            enriched.min_roas = parseFloat(value);
            convertedChanges = { max_acos: roasToAcos(enriched.min_roas) };
          }

          setRenderSave(true);
          setChanges({
            ...changes,
            [rowInfo._original?.[selectionKey!]]: {
              ...changes[rowInfo._original?.[selectionKey!]],
              [key]: value,
              updated: true,
              ...convertedChanges,
            },
          });
        };
        return enriched;
      });
    },
    // Below linting disabled to take clearFilters in to recalcalculate data on filter reset
    // Without it, filter specs from react-table must be stored at this level and manually cleared on each reset

    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [data, changes, stats, debouncedSearch, clearFilters, isExpanded, isStatsLoading, customFilters],
  );

  if (isLoading) {
    return <Loading fullPage={false} height={"500px"} />;
  }

  // Set up default table columns
  const defaultCols: Column[] = [];
  if (isSelectable && setSelected) {
    defaultCols.push(
      getSelectorColumnDefinition(selectionKey as string, selected, setSelected, disableSelectionList, hideSelectAll),
    );
  }

  if (isExpandable) {
    defaultCols.push(getExpanderColumnDefinition(isExpanded, setIsExpanded, selectionKey as string));
  }

  if (titleCol) {
    defaultCols.push(titleCol);
  }

  return (
    <>
      <Wrap gap="medium" justify={"end"}>
        {leftActionButton ? leftActionButton : null}

        {renderSave ?
          <HStack gap="medium">
            <Button
              isPending={isSaving}
              onPress={() => {
                const cb = () => {
                  setChanges({});
                };
                handleBulkChanges(changes, cb);
                setRenderSave(false);
                setSelected?.([]);
              }}
            >
              {t(($) => $.merchandising["Save Changes"])}
            </Button>
            <Button
              variant={"secondary"}
              onPress={() => {
                setRenderSave(false);
                setChanges({});
              }}
            >
              {t(($) => $.merchandising["Cancel Changes"])}
            </Button>
          </HStack>
        : null}
        <BulkActionButton
          selected={selected}
          options={bulkActionOptions.filter((o) => !o.hidden)}
          hidden={!isSelectable || bulkActionOptions.length === 0 || hideBulkActionsButton}
        />
      </Wrap>
      <ReactTable<T>
        ref={tableRef}
        noDataText={noDataText}
        showPaginationBottom={!hidePagination && !hideBottomPagination}
        showPaginationTop={!hidePagination}
        PaginationComponent={ReactTablePagination}
        data={filteredData as T[]}
        pageSizeOptions={[5, 10, 20, 25, 50]}
        defaultPageSize={savedTablePageSize || defaultPageSize}
        minRows={minRows}
        className={`-highlight ${tableId}`}
        defaultSorted={savedTableSortMethod || defaultSorted || [{ id: "sales", desc: true }]}
        filterable={showFilters}
        onSortedChange={(newSorted) => {
          setSavedTableSortMethod(newSorted);
        }}
        onPageSizeChange={(pageSize) => {
          setSavedTablePageSize(pageSize);
        }}
        // Below is needed to eliminate the no data message that covers table headers if no data to render
        NoDataComponent={() => null}
        TrComponent={TrComponent}
        getTrProps={(table: unknown, row: RowInfo | undefined) => {
          const expandedChildren = (
            <ExpandedContent marketPlace={marketPlace} channel={channel} row={row} user={user} />
          );
          return { row, expandedChildren };
        }}
        getTheadFilterProps={(props) => {
          if (clearFilters) {
            props.filtered.forEach((filter: { value: string }) => {
              filter.value = "all";
            });

            setClearFilters(false);
          }
          return { style: { display: "flex" } };
        }}
        getPaginationProps={() => {
          return {
            updateSearch: setSearchFilter,
            hideSearch: hideSearchFilter,
            hideFilterToggleIcon: hideFilterToggleIcon,
            hideResetFiltersIcon: hideResetFiltersIcon,
            clearFilters: () => {
              setSearchFilter("");
              setCustomFilters([]);

              if (typeof clearParentFilters === "function") {
                clearParentFilters();
              }

              setClearFilters(true);
            },
            hideFilters: hideFilters,
            showFilters: showFilters,
            setShowFilters: setShowFilters,
            clearSearchTermFilters: () => setSearchFilter(""),
            searchTerm: searchFilter,
            entityType: entityType,
            groupMetrics: groupMetrics,
            disableSearchFocus: true,
            filterControlsWidth: filterControlsWidth,
            columnSelectorProps: columnSelectorProps,
            setSavedColumnOrder: setSavedColumnOrder,
            overrideResultsCount: overrideResultsCount,
            children:
              filterWidget ?
                <div className="d-flex justify-content-start w-100 h-100 align-items-center px-2">
                  <RT6FilterWidget
                    customFilters={customFilters}
                    setCustomFilters={setCustomFilters}
                    tableRef={tableRef}
                    additionalFilterOptions={additionalFilterOptions}
                    excludedFilterColumns={excludedFilterColumns}
                  />
                  {children}
                </div>
              : children,
            additionalFilters: additionalFilters,
            filterWidget: filterWidget,
            canSetPageSize: canSetPageSize,
            searchTagsInput: (
              <>
                <BulkEditModal
                  title={
                    tableRowDescription ?
                      t(($) => $.merchandising["Search for {tableRowDescription}"], { tableRowDescription })
                    : t(($) => $.merchandising["Advanced Search"])
                  }
                  isOpen={showSearchModal}
                  onClose={() => setShowSearchModal(false)}
                  onSave={(values: string[]) => {
                    setSearchFilter(values.join(","));
                  }}
                  defaultValues={draftSearch.split(",")}
                />

                <Button
                  variant={"contentOnly"}
                  onPress={() => {
                    setDraftSearch(
                      searchFilter
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean)
                        .join("\n"),
                    );

                    setShowSearchModal(true);
                  }}
                  className={css({
                    position: "absolute",
                    top: "12px",
                    right: "12px",
                  })}
                >
                  <FaPencil />
                </Button>
              </>
            ),
          };
        }}
        columns={[...defaultCols, ...columns]}
      />
    </>
  );
};

export default BulkActionsTable;
